import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Chip, Drawer, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Base64 } from 'js-base64';
import React from 'react';

const OverviewComponent = ({ template, displayed, onClose }) => {
  let dangerousHTML;
  if (displayed) {
    let contentTemplate = { type: 'doc', content: [] };
    if (template?.base64Template) {
      const jsonTemplate = Base64.decode(template?.base64Template);
      contentTemplate = JSON.parse(jsonTemplate);
    }
    if (template?.content) {
      contentTemplate = template?.content;
    }
    dangerousHTML = contentTemplate?.content
      ?.map((block) => {
        if (block.type === 'paragraph') {
          if (block.content) {
            if (block.content[0]?.type === 'image') {
              return `<div style="text-align: ${block.attrs.textAlign}">
              <img src="${block.content[0].attrs.src}"
                   alt="${block.content[0].attrs.alt}"
                   title="${block.content[0].attrs.title}"
                   width="${block.content[0].attrs.width}"
              />
            </div>`;
            }
            return `<div style="text-align: ${block.attrs.textAlign}">
            ${block.content.map((content) => content.text).join('')}
          </div>`;
          }
          return '<br/>';
        }
        return '';
      })
      .join('');
  }

  // Fonction pour obtenir le nom de fichier tronqué (basée sur Dropzone.jsx)
  const getFileName = (attachment) => {
    const fileName = attachment?.file?.name || attachment?.name;
    if (!fileName) return '';

    const maxLength = 17; // Limite de caractères pour le nom du fichier

    if (fileName.length <= maxLength) return fileName;

    // Sépare le nom du fichier et son extension
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // Pas d'extension
      return `${fileName.substring(0, maxLength)}...`;
    }

    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    // Garde l'extension et tronque le nom
    const truncatedLength = maxLength - extension.length - 3; // -3 pour "..."
    return `${name.substring(0, truncatedLength)}...${extension}`;
  };

  // Fonction pour filtrer et dédupliquer les attachments (basée sur Dropzone.jsx)
  const filterAttachments = (attachments) => {
    if (!attachments || !Array.isArray(attachments)) return [];

    // Fonction utilitaire pour obtenir le nom du fichier (priorité à file.name)
    const getFileName = (attachment) => {
      return attachment?.file?.name || attachment?.name;
    };

    // Fonction utilitaire pour obtenir les propriétés du fichier
    const getFileProperties = (attachment) => {
      return {
        name: getFileName(attachment),
        size: attachment?.file?.size || attachment?.size,
        type:
          attachment?.file?.type || attachment?.type || attachment?.mimeType,
        content: attachment?.content,
        link: attachment?.link,
      };
    };

    // Filtrer les doublons en gardant uniquement la première occurrence
    const uniqueAttachments = attachments?.reduce((acc, current) => {
      const currentProps = getFileProperties(current);
      // Vérifier si un doublon existe déjà
      const isDuplicate = acc.some((existing) => {
        const existingProps = getFileProperties(existing);
        return (
          // Vérification par nom et taille
          (existingProps.name === currentProps.name &&
            existingProps.size === currentProps.size) ||
          // Vérification par contenu
          (currentProps.content &&
            existingProps.content === currentProps.content) ||
          // Vérification par lien
          (currentProps.link && existingProps.link === currentProps.link)
        );
      });

      // Ajouter seulement si ce n'est pas un doublon
      if (!isDuplicate && currentProps.name) {
        acc.push(current);
      }

      return acc;
    }, []);

    return uniqueAttachments;
  };

  return (
    <Box>
      <Drawer
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        anchor="right"
        open={displayed}
        onClose={onClose}
      >
        <Box
          sx={{
            minWidth: 800,
            maxWidth: 1500,
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              borderBottom: 1,
              marginBottom: 3,
              gap: 4,
            }}
          >
            <Button
              sx={{ margin: 3 }}
              startIcon={<ArrowBackIosIcon color="primary" />}
              variant="outlined"
              onClick={onClose}
            >
              Retour
            </Button>
            <VisibilityIcon fontSize="large" color="primary" />
            <Typography sx={{ fontSize: 24 }}>
              Aperçu du template d&apos;email
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  pb: 1,
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>
                    De : {template?.service?.code} {'>'}
                  </strong>{' '}
                  support@hoortrade.com
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>À :</strong> email@exemple.com
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Date :</strong>{' '}
                  {format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Objet :</strong> {template.subject}
                </Typography>
              </Box>

              <Box
                sx={{
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  minHeight: '300px',
                  maxHeight: '500px',
                  overflow: 'auto',
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    margin: '0 auto',
                  },
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: dangerousHTML }} />
              </Box>
              {/* Affichage des pièces jointes */}
              {template?.attachments && template.attachments.length > 0 && (
                <Box
                  sx={{
                    pt: 2,
                    mt: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    <strong>Pièces jointes :</strong>
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: '4px',
                    }}
                  >
                    {filterAttachments(template.attachments)?.map(
                      (attachment, idx) => (
                        <Tooltip
                          key={idx}
                          title={attachment?.file?.name || attachment?.name}
                        >
                          <Chip
                            variant="filled"
                            color="primary"
                            label={getFileName(attachment)}
                            sx={{ cursor: 'default' }}
                          />
                        </Tooltip>
                      )
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default OverviewComponent;
